import React from 'react';
import {
  type Weekday,
  type ScheduleTimeSlot,
} from '@/features/plants/schemas/schedule-plant.schema';
import { SCHEDULE_TIME_SLOT_WINDOW_VERTICAL_INSET_PX } from '@/features/plants/components/plant-schedule/schedule/constants.ts';
import { cn } from '@/utils/utils';
import { getPowerSetpointType, POWER_SETPOINT_COLORS } from './schedule-time-slot-colors.ts';

export interface ScheduleTimeSlotWindow {
  slot: ScheduleTimeSlot;
  day: Weekday;
  top: number;
  left: number;
  width: number;
  height: number;
}

export function ScheduleTimeSlotWindow({
  timeSlot,
  onTimeSlotClick,
  onStartMove,
  onStartResizeLeft,
  onStartResizeRight,
  dayColumnWidthPx = 132,
  suppressClicks = false,
}: {
  timeSlot: ScheduleTimeSlotWindow;
  onTimeSlotClick: (day: Weekday, slot: ScheduleTimeSlot, e: React.MouseEvent) => void;
  onStartMove: (timeSlot: ScheduleTimeSlotWindow, e: React.MouseEvent) => void;
  onStartResizeLeft: (timeSlot: ScheduleTimeSlotWindow, e: React.MouseEvent) => void;
  onStartResizeRight: (timeSlot: ScheduleTimeSlotWindow, e: React.MouseEvent) => void;
  dayColumnWidthPx?: number;
  suppressClicks?: boolean;
}) {
  const powerType = getPowerSetpointType(timeSlot.slot.powerSetpoint);
  const colors = POWER_SETPOINT_COLORS[powerType];

  return (
    <div
      className={cn(
        'group absolute z-10 flex cursor-grab flex-col items-start justify-start rounded border-2 py-1 opacity-90 shadow-sm transition-colors',
        colors.border,
        colors.bg,
        colors.bgHover
      )}
      style={{
        top: `${timeSlot.top + SCHEDULE_TIME_SLOT_WINDOW_VERTICAL_INSET_PX}px`,
        left: `${dayColumnWidthPx + timeSlot.left}px`,
        width: `${timeSlot.width}px`,
        height: `${Math.max(
          0,
          timeSlot.height - SCHEDULE_TIME_SLOT_WINDOW_VERTICAL_INSET_PX * 2
        )}px`,
      }}
      onMouseDown={(e) => onStartMove(timeSlot, e)}
      onClick={(e) => {
        e.stopPropagation();
        if (suppressClicks) return;
        onTimeSlotClick(timeSlot.day, timeSlot.slot, e);
      }}
      title={`${timeSlot.slot.startTime} - ${timeSlot.slot.endTime} (${timeSlot.slot.powerSetpoint}kW) - Click to edit`}
    >
      <div
        className="absolute top-0 left-0 h-full w-2 cursor-ew-resize rounded-l"
        onMouseDown={(e) => {
          e.stopPropagation();
          onStartResizeLeft(timeSlot, e);
        }}
      />
      <div
        className="absolute top-0 right-0 h-full w-2 cursor-ew-resize rounded-r"
        onMouseDown={(e) => {
          e.stopPropagation();
          onStartResizeRight(timeSlot, e);
        }}
      />

      <div className="pointer-events-none flex h-full w-full flex-col items-center justify-center">
        <span
          className={cn(
            'bg-opacity-90 rounded bg-white px-2 py-1 text-xs font-semibold shadow-sm',
            colors.text
          )}
        >
          {timeSlot.slot.powerSetpoint}kW
        </span>

        <div
          className={cn(
            'flex h-0.5 translate-y-1 items-center justify-center',
            timeSlot.width > 64 ? 'w-full' : 'w-fit'
          )}
        >
          <div className="bottom-0 hidden w-full justify-between gap-2 px-1 text-[10px] leading-tight font-medium text-black/80 group-hover:flex">
            <span>{timeSlot.slot.startTime}</span>
            <span>{timeSlot.slot.endTime}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
